import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Caption } from '@/ui';
import { parentApi } from './api';

/**
 * Parent Login V2 — keypad numérique 9 boutons.
 *
 * Port du proto §ParentLogin (clavier 1-9 + clear + 0 + backspace + dots
 * d'affichage 4 cercles). Plus ergonomique sur tablette que l'input HTML5
 * MVP1.2 — Valentin saisit le PIN avec un doigt rapidement.
 *
 * PIN : 4 à 8 chiffres. Le cookie parent_sid HttpOnly+Secure+SameSite=Strict
 * est posé par le backend après login OK.
 */
export function ParentLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function addDigit(d: string): void {
    if (pin.length >= 8 || pending) return;
    setPin((p) => p + d);
    setError(null);
  }
  function clear(): void {
    setPin('');
    setError(null);
  }
  function backspace(): void {
    setPin((p) => p.slice(0, -1));
    setError(null);
  }

  async function submit(): Promise<void> {
    if (!/^\d{4,8}$/.test(pin)) {
      setError('PIN doit faire 4 à 8 chiffres.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await parentApi.login(pin);
      navigate('/parent/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PIN invalide.');
      setPin('');
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto max-w-md px-6 py-12 flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <Caption>Espace parent · lecture</Caption>
        <h1 className="font-display text-display-2 text-paper-50">Vue parent</h1>
        <p className="text-body-sm text-ink-300">
          Accès en lecture aux KPIs de Léon. Le dashboard ne montre jamais d'évaluation
          négative à l'enfant.
        </p>
      </header>

      <section className="bg-ink-800 border border-ink-700 rounded-xl p-7 flex flex-col gap-5">
        <Caption>PIN parent</Caption>

        {/* PIN dots — 4 cercles + suffix si >4 chiffres */}
        <div className="flex justify-center gap-2.5">
          {[0, 1, 2, 3].map((i) => {
            const filled = i < pin.length;
            return (
              <div
                key={i}
                className={`w-14 h-14 rounded-xl flex items-center justify-center bg-ink-800 border transition-all duration-150 ${
                  filled ? 'border-paper-50 border-2' : 'border-ink-700'
                }`}
              >
                {filled && (
                  <span aria-hidden="true" className="w-3.5 h-3.5 rounded-full bg-paper-50" />
                )}
              </div>
            );
          })}
        </div>
        {pin.length > 4 && (
          <p className="text-center font-mono text-xs text-ink-300 -mt-2">
            +{pin.length - 4} chiffre{pin.length - 4 === 1 ? '' : 's'}
          </p>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-2.5">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <KeypadButton key={d} onClick={() => addDigit(String(d))}>
              {d}
            </KeypadButton>
          ))}
          <KeypadButton onClick={clear} variant="util" ariaLabel="Effacer">
            ✕
          </KeypadButton>
          <KeypadButton onClick={() => addDigit('0')}>0</KeypadButton>
          <KeypadButton onClick={backspace} variant="util" ariaLabel="Retour arrière">
            ⌫
          </KeypadButton>
        </div>

        {error && (
          <p className="text-center text-sm text-coral-500">{error}</p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={pending || pin.length < 4}
          className="w-full h-12 rounded-lg bg-paper-50 text-ink-900 font-display font-bold text-base transition-all duration-150 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {pending ? 'Vérification…' : 'Entrer'}
        </button>
      </section>

      <footer className="text-center">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
        >
          ← Retour Léon
        </button>
      </footer>
    </main>
  );
}

function KeypadButton({
  children,
  onClick,
  variant = 'default',
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'util';
  ariaLabel?: string;
}): JSX.Element {
  const variantClass =
    variant === 'util'
      ? 'bg-ink-900 border-ink-700 text-ink-300 hover:border-coral-500/40 hover:text-coral-500'
      : 'bg-ink-800 border-ink-700 text-paper-50 hover:border-paper-50/50';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`h-14 rounded-lg border font-display text-xl font-semibold transition-all duration-150 active:scale-95 ${variantClass}`}
    >
      {children}
    </button>
  );
}
